import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { useApp } from '../context/AppContext';
import server from '../services/server';
import useErrors from './useErrors';
import { useEffect, useMemo, useRef } from 'react';

/** Não retenta erros de cliente (4xx); só rede/5xx, e no máximo 1 vez. */
function shouldRetryOnError(error) {
    const status = error?.response?.status;
    if (status && status >= 400 && status < 500) return false;
    return true;
}

function useReportFetchError(error) {
    const { setErrors } = useErrors();
    const lastKeyRef = useRef(null);

    useEffect(() => {
        if (!error) {
            lastKeyRef.current = null;
            return;
        }

        const key = [
            error?.response?.status,
            typeof error?.response?.data?.message === 'string'
                ? error.response.data.message
                : JSON.stringify(error?.response?.data?.message ?? null),
            error?.message,
            error?.config?.url,
        ].join('|');

        if (lastKeyRef.current === key) return;
        lastKeyRef.current = key;
        setErrors(error);
    }, [error, setErrors]);
}

export function useFetch(url, requestConfig) {
    const { setLoading } = useApp();
    const swrKey = url ? { url, params: requestConfig } : null;

    const { data, error, mutate, isLoading, isValidating } = useSWR(
        swrKey,
        async ({ url, params }) => {
            setLoading(true);
            try {
                const response = await server.get(url, params);
                return response?.data;
            } finally {
                setLoading(false);
            }
        },
        {
            shouldRetryOnError,
            errorRetryCount: 1,
            errorRetryInterval: 5000,
            revalidateOnFocus: false,
        },
    );

    useReportFetchError(error);

    return { data: data?.data, error, mutate, isFetching: !data && !error, isLoading, isValidating };
}

export function useFetchInfinite(url, requestConfig) {
    const { data, error, size, setSize, mutate, isLoading, isValidating } = useSWRInfinite(
        (pageIndex, previousPageData) => {
            if (!url) return null;

            // Corpo padrão da API: { message, data: { data: [], page, pages, total, ... } }
            if (pageIndex > 0 && previousPageData?.data) {
                const totalPages = Number(previousPageData.data.pages);
                if (Number.isFinite(totalPages) && pageIndex >= totalPages) {
                    return null;
                }
            }

            return { url: `${url}?page=${pageIndex}`, params: requestConfig };
        },
        async ({ url, params }) => {
            const response = await server.get(url, params);
            return response.data;
        },
        {
            shouldRetryOnError,
            errorRetryCount: 1,
            errorRetryInterval: 5000,
            revalidateOnFocus: false,
            initialSize: 1,
        },
    );

    useReportFetchError(error);

    const onRechead = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return [];
        return data.flatMap(pageBody => {
            const inner = pageBody?.data;
            if (!inner) return [];
            const rows = inner.data ?? inner.rows ?? [];
            return Array.isArray(rows) ? rows : [];
        });
    }, [data]);

    const lastMeta = useMemo(() => {
        if (!Array.isArray(data) || data.length === 0) return null;
        return data[data.length - 1]?.data ?? null;
    }, [data]);

    const hasMore = useMemo(() => {
        if (!lastMeta || typeof lastMeta.pages !== 'number') return false;
        const currentPage = typeof lastMeta.page === 'number' ? lastMeta.page : data.length - 1;
        return currentPage + 1 < lastMeta.pages;
    }, [data, lastMeta]);

    return {
        data: onRechead,
        error,
        isLoading,
        isValidating,
        mutate,
        page: size,
        pages: lastMeta?.pages ?? 0,
        setPage: setSize,
        hasMore: hasMore,
        total: lastMeta?.total ?? 0,
        loadMore: () => {
            if (!hasMore) return;
            setSize(prev => prev + 1);
        },
    };
}
